import * as THREE from "three";

export const NATIVE_MATERIAL_MODELS = [
    "1a35136c-42d8-46c7-a189-c4b32da87832",
    "0c6099ed-c0db-4dd7-bc46-1d720324db5a",
    "c2cc71ce-89e7-4c94-9441-eec86ec67a60",
    "2db1d398-0161-46cd-8aff-53008d29a9dc",
    "01db85dc-81e0-4433-9440-a54ce4976178",
    "be0dcf0f-c72b-48f7-a462-6ba7560498d7",
];

export function shouldUseNativeMaterials(modelId) {
    if (!modelId) {
        console.warn(
            "[NativeMaterial] No modelId provided to shouldUseNativeMaterials",
        );
        return false;
    }
    const useNative = NATIVE_MATERIAL_MODELS.includes(modelId);
    if (useNative) {
        console.log(
            `[NativeMaterial] Model ${modelId} is configured to use native FBX materials`,
        );
    }
    return useNative;
}

/**
 * Task #331 — engine-safe GLB materials.
 *
 * Every roster GLB ships KHR_materials_specular (often + KHR_materials_ior),
 * so GLTFLoader promotes EVERY material to MeshPhysicalMaterial. The physical
 * shader permutations proved engine-dependent in production: on some
 * Chrome/ANGLE (D3D) and software (SwiftShader) backends individual programs
 * fail and their meshes silently vanish — dropped irises/teeth/clothing
 * (the "white dead eyes" regression) — while WebKit/Metal renders the same
 * file perfectly.
 *
 * Fix: downgrade physical materials to plain MeshStandardMaterial — but ONLY
 * where the risk lives (founder directive: keep full physical quality on
 * WebKit/Metal, i.e. iPad/Safari). `physicalMaterialsRisky()` gates the
 * preemptive downgrade to desktop Chromium and software-GL backends, plus any
 * device that previously demonstrated a physical-program failure (local
 * flag). Everywhere else keeps MeshPhysicalMaterial and relies on the runtime
 * health check (`physicalProgramFailureDetected`) to self-heal + remember.
 * Specular/ior/clearcoat refinements are dropped only when downgrading;
 * transmission (real glass) is approximated with plain transparency so
 * glasses lenses stay see-through.
 */
export function downgradePhysicalMaterials(mesh) {
    if (!mesh || !mesh.material) return;

    const downgradeOne = (mat) => {
        if (!mat || !mat.isMeshPhysicalMaterial) return mat;

        // Task #340 (no-invisible-start ruling): a downgrade clone must be
        // BORN at its authored blend state. When the source material
        // carries the Task #331 authored-blend stamp, copy
        // opacity/transparent/depthWrite from the STAMP — never from live
        // values some stray writer may have zeroed. Unstamped materials
        // (load-time downgrade runs before stamping) copy live values,
        // which at that point are the file-authored ones.
        const authored =
            mat.userData && mat.userData.__authoredBlend
                ? mat.userData.__authoredBlend
                : null;

        const std = new THREE.MeshStandardMaterial();
        std.name = mat.name;
        if (mat.color) std.color.copy(mat.color);
        std.map = mat.map || null;
        std.normalMap = mat.normalMap || null;
        if (mat.normalScale) std.normalScale.copy(mat.normalScale);
        std.roughness = mat.roughness != null ? mat.roughness : 1;
        std.metalness = mat.metalness != null ? mat.metalness : 0;
        std.roughnessMap = mat.roughnessMap || null;
        std.metalnessMap = mat.metalnessMap || null;
        std.aoMap = mat.aoMap || null;
        std.aoMapIntensity =
            mat.aoMapIntensity != null ? mat.aoMapIntensity : 1;
        if (mat.emissive) std.emissive.copy(mat.emissive);
        std.emissiveMap = mat.emissiveMap || null;
        std.emissiveIntensity =
            mat.emissiveIntensity != null ? mat.emissiveIntensity : 1;
        std.alphaMap = mat.alphaMap || null;
        std.transparent = authored
            ? authored.transparent === true
            : !!mat.transparent;
        std.opacity = authored
            ? typeof authored.opacity === "number"
                ? authored.opacity
                : 1
            : mat.opacity != null
              ? mat.opacity
              : 1;
        std.alphaTest = mat.alphaTest || 0;
        std.side = mat.side;
        std.depthWrite = authored
            ? authored.depthWrite !== false
            : mat.depthWrite;
        std.envMapIntensity =
            mat.envMapIntensity != null ? mat.envMapIntensity : 1;
        std.vertexColors = mat.vertexColors || false;

        // KHR_materials_transmission (real glass) has no standard-material
        // equivalent — approximate with plain transparency so lenses don't
        // turn opaque.
        const glassApprox = !!(mat.transmission && mat.transmission > 0.01);
        if (glassApprox) {
            std.transparent = true;
            std.opacity = Math.min(
                std.opacity,
                Math.max(0.15, 1 - mat.transmission),
            );
            std.depthWrite = false;
        }

        std.userData = mat.userData || {};
        // The glass approximation is a PERSISTENT blend change: per the
        // authored-blend contract it must update the stamp too, or the
        // next settle would lawfully revert the lenses to opaque.
        if (glassApprox && std.userData.__authoredBlend) {
            std.userData.__authoredBlend = {
                opacity: std.opacity,
                transparent: true,
                depthWrite: false,
            };
        }
        console.log(
            `[GLB-Sanitize] "${mesh.name}" material "${mat.name}": ${mat.type} → MeshStandardMaterial`,
        );
        mat.dispose();
        return std;
    };

    if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map(downgradeOne);
    } else {
        mesh.material = downgradeOne(mesh.material);
    }
}

const PHYSICAL_FALLBACK_FLAG = "legendlabs.physicalMaterialsBroken";
let _physicalRiskCache; // undefined = not yet computed; null = safe; string = reason

function detectGlRendererString() {
    try {
        const c = document.createElement("canvas");
        const gl = c.getContext("webgl2") || c.getContext("webgl");
        if (!gl) return "no-webgl";
        const ext = gl.getExtension("WEBGL_debug_renderer_info");
        const r = ext
            ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
            : gl.getParameter(gl.RENDERER);
        return String(r || "unknown");
    } catch (e) {
        return "gl-detect-error";
    }
}

/**
 * Task #331 scope decision (founder): preemptively downgrade ONLY where
 * physical programs are effectively guaranteed unusable —
 *   1. this device already demonstrated a physical-program failure (flag),
 *   2. software rasterizers (SwiftShader/llvmpipe) — VM/headless graphics.
 * Phase 2: desktop Chromium is NOT blanket-downgraded anymore; it runs a
 * one-time preflight compile test (`physicalPreflightFailed`) so capable
 * Chrome machines keep the premium physical shader, and only machines that
 * fail the test downgrade (and remember). Safari/WebKit (incl. iPad) keeps
 * full MeshPhysicalMaterial quality, guarded by the runtime health watch.
 * Returns a reason string when preemptive downgrade should apply, else null.
 */
export function physicalMaterialsRisky() {
    if (_physicalRiskCache !== undefined) return _physicalRiskCache;
    let reason = null;
    try {
        if (localStorage.getItem(PHYSICAL_FALLBACK_FLAG) === "1") {
            reason = "device-flagged";
        }
    } catch (e) {
        /* storage unavailable — fall through */
    }
    if (
        !reason &&
        /SwiftShader|llvmpipe|Software/i.test(detectGlRendererString())
    ) {
        reason = "software-gl";
    }
    _physicalRiskCache = reason;
    if (reason) {
        console.log(
            `[GLB-Sanitize] physical→standard downgrade active (${reason})`,
        );
    }
    return reason;
}

/** Desktop Chrome/Edge/Chromium (not mobile) — the family the dead-eyes failure was reported on. */
export function isDesktopChromiumUA() {
    const ua = navigator.userAgent || "";
    const isChromiumUA = /Chrome\/|Chromium\/|Edg\/|HeadlessChrome/.test(ua);
    const isMobileUA = /Android|iPhone|iPad|Mobile/i.test(ua);
    return isChromiumUA && !isMobileUA;
}

/** Remember on this device that physical programs failed, so future loads downgrade preemptively. */
export function rememberPhysicalMaterialsBroken() {
    _physicalRiskCache = "device-flagged";
    try {
        localStorage.setItem(PHYSICAL_FALLBACK_FLAG, "1");
    } catch (e) {
        /* best effort */
    }
}

/** True when any WebGL program in this renderer failed to compile/link (three sets `diagnostics` only on failure). */
export function physicalProgramFailureDetected(renderer) {
    const progs = renderer && renderer.info && renderer.info.programs;
    if (!progs || !progs.length) return false;
    return progs.some((p) => p.diagnostics && p.diagnostics.runnable === false);
}

let _preflightFailed = null; // once per session

/**
 * Phase 2 (Task #331): one-time capability test for desktop Chromium.
 * Compiles + renders a tiny MeshPhysicalMaterial (specular/ior set, like the
 * roster GLBs carry) into an offscreen 8×8 target, then checks two failure
 * modes:
 *   1. Program-level failure: renderer.info.programs has runnable===false
 *      (compilation/link error — caught by physicalProgramFailureDetected).
 *   2. Silent blank render: the shader "runs" (no program error) but outputs
 *      all-zero pixels — seen on some Chrome/ANGLE (D3D) machines where the
 *      physical specular permutation produces invisible geometry without any
 *      WebGL error. Marcus Aurelius (8/8 physical materials) is the canonical
 *      case: preflight without pixel-read silently passed on these machines.
 * Either failure → remember on this device (preemptive downgrade) and report true.
 * NOTE: this tests a simple non-skinned permutation; heavier skinned/morph-target
 * permutations can still fail on marginal hardware. The runtime health watch in
 * ModelViewer.animate() remains the second guard for those cases.
 */
export function physicalPreflightFailed(renderer) {
    if (_preflightFailed !== null) return _preflightFailed;
    if (!renderer) return (_preflightFailed = false);
    let mesh, rt;
    try {
        const scene = new THREE.Scene();
        const cam = new THREE.PerspectiveCamera(50, 1, 0.1, 10);
        cam.position.z = 2;
        mesh = new THREE.Mesh(
            new THREE.SphereGeometry(0.5, 8, 8),
            new THREE.MeshPhysicalMaterial({
                specularIntensity: 0.7,
                specularColor: new THREE.Color(0xffffff),
                ior: 1.45,
                roughness: 0.4,
                metalness: 0.1,
            }),
        );
        scene.add(mesh);
        scene.add(new THREE.DirectionalLight(0xffffff, 1));
        rt = new THREE.WebGLRenderTarget(8, 8);
        const prev = renderer.getRenderTarget();
        renderer.setRenderTarget(rt);
        renderer.render(scene, cam);
        renderer.setRenderTarget(prev);

        // Guard 1: compile/link failure.
        const programFailed = physicalProgramFailureDetected(renderer);

        // Guard 2: silent blank render — shader compiled but outputs black.
        // A correctly-rendered lit sphere must produce at least one non-trivial
        // pixel. Read 4 pixels near the sphere's centre (offset from corners
        // to avoid background). Any non-zero value in any channel means the
        // physical shader is producing visible output.
        let blankRender = false;
        try {
            const buf = new Uint8Array(4 * 4 * 4); // 4×4 block, RGBA bytes
            renderer.readRenderTargetPixels(rt, 2, 2, 4, 4, buf);
            const maxVal = buf.reduce((mx, v) => (v > mx ? v : mx), 0);
            if (maxVal < 8) {
                // All pixels effectively black — physical shader is invisible.
                blankRender = true;
                console.warn(
                    "[GLB-Sanitize] physical preflight: blank render (max pixel value " +
                    maxVal + ") — physical shader produces invisible output on this GPU",
                );
            }
        } catch (pixErr) {
            // readRenderTargetPixels threw (e.g. context lost) — err on the
            // side of caution and treat as a failure so we don't strand a
            // character invisible.
            console.warn("[GLB-Sanitize] physical preflight pixel-read threw:", pixErr);
            blankRender = true;
        }

        _preflightFailed = programFailed || blankRender;
    } catch (e) {
        console.warn("[GLB-Sanitize] physical preflight threw:", e);
        _preflightFailed = true; // render blew up = do not trust physical here
    }
    try {
        if (mesh) {
            mesh.geometry.dispose();
            mesh.material.dispose();
        }
        if (rt) rt.dispose();
    } catch (e) {
        /* best effort */
    }
    if (_preflightFailed) {
        console.log(
            "[GLB-Sanitize] physical preflight FAILED — downgrading (chromium-preflight)",
        );
        rememberPhysicalMaterialsBroken();
    }
    return _preflightFailed;
}

export function logNativeMaterialInfo(mesh) {
    if (!mesh || !mesh.material) return;

    const material = mesh.material;
    const materialType = material.type || "Unknown";

    console.log(`[NativeMaterial] Mesh: "${mesh.name}"`);
    console.log(`  Material Type: ${materialType}`);

    if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
        console.log(`  Roughness: ${material.roughness}`);
        console.log(`  Metalness: ${material.metalness}`);
        console.log(`  Has Map: ${!!material.map}`);
        console.log(`  Has NormalMap: ${!!material.normalMap}`);
        console.log(`  Has RoughnessMap: ${!!material.roughnessMap}`);
        console.log(`  Has MetalnessMap: ${!!material.metalnessMap}`);
    } else if (material.isMeshPhongMaterial) {
        console.log(`  Shininess: ${material.shininess}`);
        console.log(`  Specular: ${material.specular?.getHexString()}`);
        console.log(`  Has Map: ${!!material.map}`);
        console.log(`  Has NormalMap: ${!!material.normalMap}`);
    }
}
